import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { notify } from "./Toast";
import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import ProgressBar from "./ProgressBar";
import HourAmPmSelect from "./HourAmPmSelect";
import MultiSelect from "./MultiSelect";
import { useAuth } from "../hooks/useAuth";
import { useOrgCatalog } from "../hooks/useOrgCatalog";
import { ALLOWED_BLOCK_MINUTES, } from "../api/types";
// Mirror of backend FLEX_TITLE / FLEX_DESCRIPTION — kept here so the UI shows
// the same text the server enforces.
const FLEX_TITLE = "Flex";
const FLEX_DESCRIPTION = 'Flex Boxes are built in time for when a "work fire" or unexpected time sucks occur.';
// Split a Date into (YYYY-MM-DD, HH:MM) in local time. Used to populate the
// separate date input + 15-min TimeSelect dropdown that replaced the
// browser-native datetime-local picker (which lets users scroll to any
// minute regardless of the step attribute).
function toLocalDateTimeParts(d) {
    const pad = (n) => String(n).padStart(2, "0");
    const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return { date, time };
}
// Rebuild a Date from the split (YYYY-MM-DD, HH:MM) inputs. The constructor
// interprets the inputs in local time — that's what we want, since the user
// is picking on their wall clock.
function fromLocalDateTimeParts(date, time) {
    const [y, m, d] = date.split("-").map(Number);
    const [h, mm] = time.split(":").map(Number);
    return new Date(y, (m || 1) - 1, d || 1, h || 0, mm || 0, 0, 0);
}
function diffMinutes(a, b) {
    return Math.round((b.getTime() - a.getTime()) / 60000);
}
function nearestAllowed(mins) {
    // Snap any input duration to the closest allowed value.
    let best = ALLOWED_BLOCK_MINUTES[0];
    let bestDiff = Math.abs(mins - best);
    for (const m of ALLOWED_BLOCK_MINUTES) {
        if (Math.abs(mins - m) < bestDiff) {
            best = m;
            bestDiff = Math.abs(mins - m);
        }
    }
    return best;
}
export default function BlockEditor({ open, initialStart, initialEnd, existing, prefill, onClose, onSaved, onPromptProgress, }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    // Split into separate date + time pieces so the time selector can be a
    // strict 15-min dropdown (TimeSelect) instead of the native datetime-local
    // input that the browser lets users scroll to any minute on.
    const [startDate, setStartDate] = useState("");
    const [startTime, setStartTime] = useState("09:00");
    const [duration, setDuration] = useState(30);
    const [blockType, setBlockType] = useState("lockout");
    // For a new Personal block: also drop it onto the private Personal To-Do
    // list (POST /me/personal-todos) on save. Defaults to "No" so scheduling
    // personal time doesn't silently spawn to-dos. Only shown on create.
    const [addToPersonalTodo, setAddToPersonalTodo] = useState(false);
    const [linkedItemId, setLinkedItemId] = useState(null);
    // Locked title flag — true when scheduling from a project task/item so the
    // canonical "Project — Task" label can't drift. Always false when editing an
    // existing block (the owner can re-title their own block at any time).
    const [titleLocked, setTitleLocked] = useState(false);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    // Overlap-conflict state. When the server returns 409 with a structured
    // `block_overlap` detail, we capture the conflicting block's metadata and
    // surface a "Keep existing / Replace with new" choice instead of just
    // showing an alert. Stays null when there's no conflict.
    const [conflict, setConflict] = useState(null);
    // Up to 4 alternative open slots returned by the server when a save
    // 409s on overlap — rendered as quick-pick chips so the user can
    // re-book without manually hunting for free time. Shape matches the
    // backend's `suggested_slots` array.
    const [suggestedSlots, setSuggestedSlots] = useState([]);
    // Kill List dropdown — when the user picks Deep Focus on a brand-new block,
    // they must pick *what* to focus on from their committed Kill List. Loading
    // is lazy: we only fetch on open. The selected ID maps back to a Kill List
    // row whose checklist_item_id (or task→auto-checklist) feeds linkedItemId.
    const [killList, setKillList] = useState([]);
    const [killListLoading, setKillListLoading] = useState(false);
    const [selectedKillId, setSelectedKillId] = useState("");
    // Local mirror of the linked task's progress so the bar and Complete button
    // update instantly after the user PATCHes /tasks/{id}, without waiting for
    // the parent calendar to reload.
    const [taskProgress, setTaskProgress] = useState(null);
    // Project info for the linked block — fetched on open so the modal can
    // show the project's team (assignee names) alongside the "Open full
    // project" deep-link. Stays null when the block isn't project-scoped.
    const [linkedProject, setLinkedProject] = useState(null);
    // Co-assignees on the linked task — fed by GET /tasks/{id}/assignees
    // when the block is linked to a task. Drives the "Also add to
    // teammates' calendars" mirror prompt for multi-assignee meetings
    // (e.g. a Discovery call). `is_self` excluded from the prompt copy.
    const [taskCoAssignees, setTaskCoAssignees] = useState([]);
    // Which co-assignees the caller wants to mirror this block to.
    // Defaults to "all others on the task" — the common case is a meeting
    // where everyone should land at the same time. User can deselect any.
    const [mirrorTargets, setMirrorTargets] = useState(new Set());
    // ── One-off task creation ─────────────────────────────────────────────
    // "Track this block as" segmented control — only meaningful for NEW
    // blocks. "oneoff" swaps the editor body to the one-off task creator.
    const [trackAs, setTrackAs] = useState("block");
    // The one-off task's name + its merged BU/activity category. The
    // category value is "" (none), "bu:<id>", or "act:<id>". Reused for the
    // one-off meeting flow (meeting name + category).
    const [oneOffTitle, setOneOffTitle] = useState("");
    const [oneOffCategory, setOneOffCategory] = useState("");
    const [oneOffDue, setOneOffDue] = useState("");
    // Meeting flow state. meetingKind toggles one-off vs recurring (recurring
    // hands off to the New Project form, where the cadence editor lives).
    // meetingAttendees are the invited users; meetingAddMe adds the caller.
    const { user: authUser } = useAuth();
    const [meetingKind, setMeetingKind] = useState("oneoff");
    const [meetingAttendees, setMeetingAttendees] = useState([]);
    const [meetingAddMe, setMeetingAddMe] = useState(true);
    const [meetingPeople, setMeetingPeople] = useState([]);
    const [meetingPeopleLoaded, setMeetingPeopleLoaded] = useState(false);
    // Catalog for the merged Category dropdown — shared cached hook (one fetch
    // per session, deduped across every consumer).
    const { businessUnits, activities } = useOrgCatalog();
    // When a one-off save creates the task but the block POST hits an
    // overlap conflict, we stash the task id here so the conflict-resolve
    // retry reuses it instead of spawning a duplicate one-off task.
    const [oneOffTaskId, setOneOffTaskId] = useState(null);
    // Personal is a real block_type now (not a separate checkbox). For values
    // (existing blocks / prefills) that pre-date migration 0010 and use
    // is_personal=true, promote them to block_type="personal" so the dropdown
    // shows the right option. Returns `undefined` when nothing is known so the
    // caller can fall back to its own duration-based default.
    function promotePersonal(t, isPersonal) {
        if (isPersonal)
            return "personal";
        return t ?? undefined;
    }
    useEffect(() => {
        if (!open)
            return;
        // Project work = scheduling against a known task / checklist item / kill
        // row. Drives BOTH the default tab and the default block type so they
        // can't disagree on first paint — the bug where a brand-new
        // Flex/Personal block rendered Deep Focus + "From your Kill List" fields
        // until you toggled tabs (default trackAs was "block" but default
        // blockType was "lockout").
        const prefillIsProjectWork = !!prefill?.checklist_item_id ||
            !!prefill?.killListItemId ||
            !!prefill?.taskId;
        if (existing) {
            setTitle(existing.title);
            setDescription(existing.description ?? "");
            const parts = toLocalDateTimeParts(new Date(existing.start_at));
            setStartDate(parts.date);
            setStartTime(parts.time);
            const existingDur = nearestAllowed(diffMinutes(new Date(existing.start_at), new Date(existing.end_at)));
            // Treat legacy is_personal=true rows as personal-type so the dropdown
            // shows the right option for blocks created before migration 0010.
            const resolvedType = promotePersonal(existing.block_type, existing.is_personal) ?? "lockout";
            // Deep Focus is capped at 60 — snap any legacy block longer than that
            // back to 60 so the duration buttons stay in sync with the editor.
            setDuration(resolvedType === "lockout" && existingDur > 60
                ? 60
                : existingDur);
            setBlockType(resolvedType);
            setLinkedItemId(existing.checklist_item_id);
            // Existing blocks: the owner can rename their own block freely.
            setTitleLocked(false);
            setTaskProgress(existing.task_progress_percent);
        }
        else if (initialStart) {
            const d = prefill?.duration_minutes
                ? nearestAllowed(prefill.duration_minutes)
                : initialEnd
                    ? nearestAllowed(diffMinutes(initialStart, initialEnd))
                    : 30;
            setTitle(prefill?.title ?? "");
            setDescription(prefill?.description ?? "");
            const parts = toLocalDateTimeParts(initialStart);
            setStartDate(parts.date);
            setStartTime(parts.time);
            setDuration(d);
            // Prefill-driven type takes precedence; otherwise default to match the
            // default tab — Deep Focus only when this is project work (the Kill
            // List tab), else Personal (the Flex/Personal tab that "+ New block"
            // opens on now defaults to Personal). Keying off prefillIsProjectWork
            // instead of duration keeps the type and the selected tab in sync on
            // first paint.
            setBlockType(promotePersonal(prefill?.block_type, prefill?.is_personal) ??
                (prefillIsProjectWork ? "lockout" : "personal"));
            setLinkedItemId(prefill?.checklist_item_id ?? null);
            setTitleLocked(prefill?.titleLocked ?? false);
            setTaskProgress(null);
        }
        setError(null);
        // Pre-select the kill-list dropdown when the prefill named a specific
        // kill row — happens when the user clicks Schedule on a Kill List card.
        // Otherwise clear so a stale ID doesn't leak across opens.
        setSelectedKillId(prefill?.killListItemId ?? "");
        // Reset the one-off task flow on every open — it's a new-block-only
        // mode, and a stale title/category shouldn't leak across opens. When
        // the prefill already points at project work (a checklist item, a
        // kill row, or a task), open on the "A project task" segment so the
        // control matches what's being scheduled. (prefillIsProjectWork is
        // computed at the top of this effect so the block type defaults in
        // lockstep with the tab.)
        setTrackAs(prefillIsProjectWork ? "project" : "block");
        setOneOffTitle("");
        setOneOffCategory("");
        setOneOffDue("");
        setOneOffTaskId(null);
        setMeetingKind("oneoff");
        setMeetingAttendees([]);
        setMeetingAddMe(true);
        // Clear any leftover slot suggestions from a previous open. The
        // offerSlots effect below repopulates them for the reschedule flow.
        setSuggestedSlots([]);
    }, [existing, initialStart, initialEnd, open, prefill]);
    // Lazily load the people picker the first time the user opens Meeting
    // mode — the invitee list for a one-off meeting.
    useEffect(() => {
        if (!open || trackAs !== "meeting" || meetingPeopleLoaded)
            return;
        let cancelled = false;
        (async () => {
            try {
                const people = await api.get("/users/coworkers");
                if (!cancelled) {
                    setMeetingPeople(people);
                    setMeetingPeopleLoaded(true);
                }
            }
            catch {
                /* non-fatal — the picker just stays empty */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, trackAs, meetingPeopleLoaded]);
    // Load the caller's Kill List once when the modal opens — the Deep Focus
    // dropdown reads from this. Failures fall through silently; the dropdown
    // will just be empty (with a "Nothing on your kill list" placeholder).
    useEffect(() => {
        if (!open)
            return;
        let cancelled = false;
        (async () => {
            setKillListLoading(true);
            try {
                const list = await api.get("/kill-list");
                if (cancelled)
                    return;
                setKillList(list);
                // Auto-select the kill row that matches what we're rescheduling.
                // Reschedule from "Not yet" forwards the original block's task_id
                // (and checklist_item_id when present) — without this match, the
                // dropdown opens at "Pick a Kill List item…" even though the row
                // is right there, which read as "task didn't load" in the field
                // report. Skip when prefill.killListItemId already named a row,
                // or when the user manually changed the dropdown before the
                // fetch landed.
                if (!prefill?.killListItemId && !selectedKillId) {
                    const match = list.find((k) => {
                        if (prefill?.checklist_item_id &&
                            k.checklist_item_id === prefill.checklist_item_id) {
                            return true;
                        }
                        if (prefill?.taskId && k.task_id === prefill.taskId) {
                            return true;
                        }
                        return false;
                    });
                    if (match) {
                        setSelectedKillId(match.id);
                        const label = match.project_name
                            ? `${match.project_name} — ${match.title}`
                            : match.title;
                        setTitle(label);
                        setTitleLocked(true);
                        setLinkedItemId(match.checklist_item_id);
                    }
                }
            }
            catch {
                /* ignore — dropdown stays empty */
            }
            finally {
                if (!cancelled)
                    setKillListLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // selectedKillId is intentionally NOT a dep — re-running on every
        // selection change would clobber the user's manual pick.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, prefill?.taskId, prefill?.checklist_item_id, prefill?.killListItemId]);
    // Proactive open-slot suggestions for the reschedule flow. When the
    // prefill asks for them (offerSlots), fetch the caller's nearest free
    // windows that fit the prefilled duration and stash them in
    // suggestedSlots so the editor body renders quick-pick chips — the user
    // gets the closest times to drop the follow-up into instead of hunting
    // for a gap. Only for NEW blocks (a reschedule always creates one).
    useEffect(() => {
        if (!open || existing || !prefill?.offerSlots || !initialStart) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const dur = prefill.duration_minutes ?? 30;
                const res = await api.get(`/time-blocks/open-slots?start=${encodeURIComponent(initialStart.toISOString())}&duration_minutes=${dur}`);
                if (!cancelled)
                    setSuggestedSlots(res.slots ?? []);
            }
            catch {
                /* ignore — the editor still works, just without the chips */
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, existing, prefill?.offerSlots, prefill?.duration_minutes]);
    // Apply a suggested slot to the date/time inputs. Shared by the inline
    // reschedule chips and the conflict-modal chips so both quantize + write
    // the same way.
    function applySuggestedSlot(startIso) {
        const start = new Date(startIso);
        const y = start.getFullYear();
        const m = String(start.getMonth() + 1).padStart(2, "0");
        const d = String(start.getDate()).padStart(2, "0");
        const hh = String(start.getHours()).padStart(2, "0");
        const mm = String(start.getMinutes()).padStart(2, "0");
        setStartDate(`${y}-${m}-${d}`);
        setStartTime(`${hh}:${mm}`);
        setError(null);
    }
    // Resolve the linked project (when the block we're editing carries
    // project_id) so the modal can show "Team: Alice, Bob, Carol" alongside
    // the "Open full project" deep-link. Failures fall through silently;
    // the team row just won't render.
    useEffect(() => {
        if (!open || !existing?.project_id) {
            setLinkedProject(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const p = await api.get(`/projects/${existing.project_id}`);
                if (!cancelled)
                    setLinkedProject(p);
            }
            catch {
                /* ignore — banner just won't render */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, existing?.project_id]);
    // Co-assignees on the linked task — fetched whenever the editor opens
    // with a task linkage (prefill.taskId, existing.task_id, or the
    // resolved task on an existing block's checklist item). Drives the
    // mirror prompt. Failures fall through silently — the prompt just
    // doesn't render and the caller schedules only for themselves.
    useEffect(() => {
        const taskId = prefill?.taskId ?? existing?.task_id ?? null;
        if (!open || !taskId) {
            setTaskCoAssignees([]);
            setMirrorTargets(new Set());
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const rows = await api.get(`/tasks/${taskId}/assignees`);
                if (cancelled)
                    return;
                setTaskCoAssignees(rows);
                // Default: pre-check every other assignee. The caller can
                // uncheck names before saving — un-checking everyone is the
                // "just put it on my calendar" path.
                setMirrorTargets(new Set(rows.filter((r) => !r.is_self).map((r) => r.user_id)));
            }
            catch {
                /* ignore — prompt just won't render */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, prefill?.taskId, existing?.task_id]);
    if (!open)
        return null;
    // For flex blocks, force the prescribed title/description (server enforces too).
    const isFlex = blockType === "flex";
    const isPersonal = blockType === "personal";
    // True when the editor is in one-off task creation mode.
    const isOneOff = !existing && trackAs === "oneoff";
    // True when the editor is in Meeting creation mode (new blocks only).
    const isMeeting = !existing && trackAs === "meeting";
    // Build the request payload once — used by both save() and the conflict
    // modal's "replace existing" retry path.
    function buildPayload() {
        const start = fromLocalDateTimeParts(startDate, startTime);
        const end = new Date(start.getTime() + duration * 60_000);
        // One-off task mode: the block is titled after the task and linked
        // straight to the one-off task we just created. No kill-list /
        // checklist-item linkage. (oneOffTaskId is set by saveOneOff before
        // the block POST, so the conflict-resolve retry reuses it.)
        if (isOneOff) {
            return {
                title: oneOffTitle.trim(),
                description: null,
                start_at: start.toISOString(),
                end_at: end.toISOString(),
                block_type: "lockout",
                is_personal: false,
                checklist_item_id: null,
                task_id: oneOffTaskId,
            };
        }
        // Resolve the task linkage in priority order:
        //   1. Prefill carries an explicit taskId (scheduler scheduleTaskAsBlock,
        //      task-only Kill List card click).
        //   2. The user picked a kill row in the in-modal dropdown — pull task_id
        //      off that row. Task-only kill rows have it set; checklist-item
        //      kill rows have it null (linkage flows through checklist_item_id
        //      instead).
        // Resolving here means the BlockEditor's own dropdown picks now sync
        // the kill row + flip task status + auto-delete the kill row server-
        // side, just like scheduling from the panel does.
        const selectedKill = selectedKillId
            ? killList.find((k) => k.id === selectedKillId) ?? null
            : null;
        const resolvedTaskId = prefill?.taskId ?? selectedKill?.task_id ?? null;
        // Multi-assignee mirror — only meaningful for NEW blocks (existing
        // edits don't fan out to other people) AND when the block actually
        // has a task linkage (mirroring a Flex/Personal block makes no
        // sense). Empty array = no mirror, just the caller's block.
        const shouldMirror = !existing &&
            !!resolvedTaskId &&
            blockType !== "personal" &&
            mirrorTargets.size > 0;
        const payload = {
            title: isFlex ? FLEX_TITLE : title,
            description: isFlex ? FLEX_DESCRIPTION : (description || null),
            start_at: start.toISOString(),
            end_at: end.toISOString(),
            block_type: blockType,
            // Mirror the canonical type into the legacy boolean so any server-side
            // filter that still keys off is_personal keeps working.
            is_personal: isPersonal,
        };
        // Task linkage is set ONLY when creating a new block. On an EXISTING
        // block (e.g. rescheduling a meeting through this editor) we must NOT
        // send task_id / checklist_item_id — the editor doesn't expose changing
        // them, and resolvedTaskId is null for an existing block, so sending it
        // would null out the block's task link. That orphaning is exactly what
        // strands a meeting on a co-attendee's calendar so "reschedule for
        // everyone" (keyed on task_id) can no longer find it. Omitting these on
        // edits leaves the persisted linkage untouched (backend exclude_unset).
        if (!existing) {
            payload.checklist_item_id = linkedItemId;
            // Forwarded so the backend can flip the task to in_progress and wipe
            // matching kill-list rows on create. Transient — not stored on the row.
            payload.task_id = resolvedTaskId;
            payload.mirror_to_user_ids = shouldMirror
                ? Array.from(mirrorTargets)
                : [];
            // Goal session link — forwarded from the prefill when scheduling a goal
            // session, so completing the block logs units to the goal instead of
            // asking for time.
            if (prefill?.goalId) {
                payload.goal_id = prefill.goalId;
                payload.goal_session_target = prefill.goalSessionTarget ?? null;
                payload.goal_unit = prefill.goalUnit ?? null;
            }
        }
        return payload;
    }
    // One-off task save: create the one_off_task project + task, then post
    // the time block linked to it. Bypasses the Deep Focus kill-list guard
    // — a one-off task IS its own committed work. On an overlap 409 we
    // stash the created task id (oneOffTaskId) so the conflict-resolve
    // retry doesn't spawn a duplicate one-off task.
    async function saveOneOff() {
        const name = oneOffTitle.trim();
        if (!name) {
            setError("Give your one-off task a name.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            let taskId = oneOffTaskId;
            if (!taskId) {
                const body = { title: name };
                if (oneOffCategory.startsWith("bu:")) {
                    body.business_unit_id = oneOffCategory.slice(3);
                }
                else if (oneOffCategory.startsWith("act:")) {
                    body.activity_id = oneOffCategory.slice(4);
                }
                if (oneOffDue)
                    body.due_date = oneOffDue;
                const created = await api.post("/projects/one-off-task", body);
                taskId = created.task_id;
                setOneOffTaskId(taskId);
            }
            const start = fromLocalDateTimeParts(startDate, startTime);
            const end = new Date(start.getTime() + duration * 60_000);
            await api.post("/time-blocks", {
                title: name,
                description: null,
                start_at: start.toISOString(),
                end_at: end.toISOString(),
                block_type: "lockout",
                is_personal: false,
                checklist_item_id: null,
                task_id: taskId,
            });
            onSaved();
            onClose();
        }
        catch (err) {
            if (err instanceof ApiError && err.status === 409) {
                const body = err.body;
                const detail = body?.detail;
                if (detail?.code === "block_overlap" && detail.conflict) {
                    // buildPayload() handles the one-off branch, so the conflict
                    // modal's "Replace with new" retry posts the right block.
                    setConflict(detail.conflict);
                    setSuggestedSlots(detail.suggested_slots ?? []);
                    return;
                }
            }
            setError(err instanceof Error
                ? err.message
                : "Couldn't create the one-off task");
        }
        finally {
            setSaving(false);
        }
    }
    // Create a one-off meeting straight from the calendar. This spins up a
    // one_time_event project (a pure event — no task roadmap), assigns every
    // attendee, and the backend materializes a block on each attendee's
    // calendar (notifying anyone with a conflict via the Awaiting Review
    // swap proposal). Recurring meetings are handed off to the New Project
    // form, which carries the full cadence editor.
    async function saveMeeting() {
        const name = oneOffTitle.trim();
        if (!name) {
            setError("Give the meeting a name.");
            return;
        }
        const attendees = new Set(meetingAttendees);
        if (meetingAddMe && authUser?.id)
            attendees.add(authUser.id);
        if (attendees.size === 0) {
            setError("Add at least one attendee (or include yourself).");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const start = fromLocalDateTimeParts(startDate, startTime);
            const body = {
                name,
                type: "one_time_event",
                due_date: null,
                priority: 5,
                business_unit_id: oneOffCategory.startsWith("bu:")
                    ? oneOffCategory.slice(3)
                    : null,
                activity_id: oneOffCategory.startsWith("act:")
                    ? oneOffCategory.slice(4)
                    : null,
                assignee_ids: Array.from(attendees),
                event_at: start.toISOString(),
                event_duration_minutes: duration,
            };
            await api.post("/projects", body);
            onSaved();
            onClose();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Couldn't create the meeting");
        }
        finally {
            setSaving(false);
        }
    }
    async function save() {
        // One-off task mode has its own save path (create the task, then the
        // block) — it doesn't go through the Deep Focus kill-list guard.
        if (isOneOff) {
            await saveOneOff();
            return;
        }
        // Meeting mode creates a one_time_event with attendees instead of a block.
        if (isMeeting) {
            await saveMeeting();
            return;
        }
        // Deep Focus blocks must trace back to a real task or checklist item —
        // otherwise the block has no project linkage and lands in the time
        // reports as "Unassigned" forever (lockout blocks can't carry a
        // block-level BU/activity the way Flex can). This mirrors the backend
        // guard in routes/time_blocks.py create_block. Two valid link sources:
        //   1. linkedItemId — a real checklist_item_id (the block links to it
        //      and the server resolves the parent task).
        //   2. resolvedTaskId — a task_id off the prefill or the picked kill
        //      row. Task-only kill rows carry this; parking-lot-only rows DON'T,
        //      which is exactly the orphan case we're closing here.
        // A kill row with neither (e.g. a stray parking-lot row) no longer
        // counts as a valid source. The error names what to do next.
        const guardSelectedKill = selectedKillId
            ? killList.find((k) => k.id === selectedKillId) ?? null
            : null;
        const guardResolvedTaskId = prefill?.taskId ?? guardSelectedKill?.task_id ?? null;
        const hasTaskLink = !!linkedItemId || !!guardResolvedTaskId;
        if (!existing && blockType === "lockout" && !hasTaskLink) {
            setError("Deep Focus blocks must be linked to a task or checklist item. Pick a task from your Kill List, or choose Flex / Personal for free-form time.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = buildPayload();
            if (existing) {
                await api.patch(`/time-blocks/${existing.id}`, payload);
            }
            else {
                // Capture the response so we can surface mirror_results — when
                // the caller asked to mirror to teammates, the server may
                // report some as "conflict" (slot already booked on their
                // calendar) or "forbidden" (no longer a task assignee). We
                // never block the save on those; we just tell the user what
                // landed.
                const created = await api.post("/time-blocks", payload);
                const results = created?.mirror_results ?? [];
                if (results.length > 0) {
                    const created_count = results.filter((r) => r.status === "created").length;
                    const issues = results.filter((r) => r.status !== "created");
                    const nameOf = (uid) => taskCoAssignees.find((c) => c.user_id === uid)?.user_name ?? "a teammate";
                    const parts = [];
                    if (created_count > 0) {
                        parts.push(`Added to ${created_count} teammate${created_count === 1 ? "'s" : "s'"} calendar.`);
                    }
                    if (issues.length > 0) {
                        const lines = issues
                            .map((r) => `• ${nameOf(r.user_id)}: ${r.reason ?? r.status}`)
                            .join("\n");
                        parts.push(`Couldn't mirror to:\n${lines}`);
                    }
                    if (parts.length > 0) {
                        // Use alert so the message is loud — conflicts deserve a
                        // pause. Future polish: replace with an inline toast row.
                        notify.error(parts.join("\n\n"));
                    }
                }
            }
            // New Personal block + the user opted in → also drop it onto their
            // private Personal To-Do list. Best-effort: the calendar block is
            // already saved, so a to-do failure shouldn't roll that back — we just
            // warn. due_date seeds from the block's date so it lands in context.
            if (!existing && isPersonal && addToPersonalTodo) {
                try {
                    await api.post("/me/personal-todos", {
                        title: title.trim() || "Personal task",
                        description: description.trim() || null,
                        due_date: startDate || null,
                    });
                }
                catch {
                    notify.error("Block saved, but couldn't add it to your Personal To-Do list.");
                }
            }
            onSaved();
            onClose();
        }
        catch (err) {
            // 409 with a structured `block_overlap` detail → open the conflict
            // modal instead of slamming the user with a raw error string. Falls
            // through to the generic error display for any other failure.
            if (err instanceof ApiError && err.status === 409) {
                const body = err.body;
                const detail = body?.detail;
                if (detail?.code === "block_overlap" && detail.conflict) {
                    setConflict(detail.conflict);
                    setSuggestedSlots(detail.suggested_slots ?? []);
                    return; // don't set the generic error message
                }
            }
            setError(err instanceof Error ? err.message : "Save failed");
        }
        finally {
            setSaving(false);
        }
    }
    // Conflict-modal resolution: user picked "replace". Delete the conflicting
    // block, then retry the save with the same payload. Best-effort — if the
    // retry also fails (rare; would happen if a *different* block now occupies
    // the slot), surface the new error.
    async function resolveByReplacing() {
        if (!conflict)
            return;
        setSaving(true);
        setError(null);
        try {
            await api.del(`/time-blocks/${conflict.id}`);
            const payload = buildPayload();
            if (existing) {
                await api.patch(`/time-blocks/${existing.id}`, payload);
            }
            else {
                await api.post("/time-blocks", payload);
            }
            setConflict(null);
            onSaved();
            onClose();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Replace failed");
        }
        finally {
            setSaving(false);
        }
    }
    async function remove() {
        if (!existing)
            return;
        if (!confirm("Delete this block?"))
            return;
        setSaving(true);
        try {
            await api.del(`/time-blocks/${existing.id}`);
            onSaved();
            onClose();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Delete failed");
        }
        finally {
            setSaving(false);
        }
    }
    // Bump linked-task progress. Setting progress_percent to 100 server-side
    // also auto-flips task.status to completed (see Task.progress_percent docs).
    // Used by both the "Mark complete" button and any future progress slider —
    // accepts any 0-100 value so we can reuse it.
    async function setLinkedTaskProgress(next) {
        if (!existing?.task_id)
            return;
        setSaving(true);
        setError(null);
        try {
            // When the user marks the task complete on a past block, also flip
            // the block itself to is_completed so the backend cascade runs and
            // buckets the task's completed_at on the block's end day (not
            // "now"). Without this, marking a yesterday's task complete today
            // would show up under today's accomplishments. Skip when the block
            // hasn't ended yet (live work — wall-clock completion is correct).
            const blockPast = !existing.is_completed &&
                new Date(existing.end_at).getTime() <= Date.now();
            if (next === 100 && blockPast) {
                await api.patch(`/time-blocks/${existing.id}`, { is_completed: true });
            }
            else {
                await api.patch(`/tasks/${existing.task_id}`, {
                    progress_percent: next,
                });
            }
            setTaskProgress(next);
            // Refresh the calendar so other blocks linked to the same task see the
            // new progress too. Fire and forget — we don't want to delay the
            // modal close behind the parent's refetch.
            onSaved();
            // Auto-close after a hit-100 click so the user sees the calendar
            // update instantly instead of having to dismiss the editor first.
            // Partial progress updates leave the editor open so the user can
            // keep tweaking.
            if (next === 100)
                onClose();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Couldn't update progress");
        }
        finally {
            setSaving(false);
        }
    }
    async function removeWholeSeries() {
        if (!existing?.recurring_pattern_id)
            return;
        if (!confirm("Delete every occurrence of this recurring meeting on your calendar? " +
            "This removes the recurring pattern and every materialized block. Cannot be undone.")) {
            return;
        }
        setSaving(true);
        try {
            await api.del(`/recurring/${existing.recurring_pattern_id}`);
            onSaved();
            onClose();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Delete failed");
        }
        finally {
            setSaving(false);
        }
    }
    // ── Shared-meeting delete scoping ─────────────────────────────────
    // A meeting block belongs to a recurring_event / one_time_event
    // project; it's "shared" when its task carries 2+ assignees. For
    // these we surface a "Cancel for everyone" option alongside the
    // existing my-calendar-only deletes (single occurrence / my series /
    // my block) so deleting asks whether to remove it for just you or the
    // whole team. recurring_pattern_id covers recurring meetings even if
    // project_type isn't hydrated on older payloads.
    const isMeetingBlock = !!existing &&
        (!!existing.recurring_pattern_id ||
            existing.project_type === "recurring_event" ||
            existing.project_type === "one_time_event");
    const isSharedMeeting = isMeetingBlock && (existing?.task_assignee_names?.length ?? 0) > 1;
    // Only management-tier users can cancel a meeting for everyone. The
    // backend re-checks per-BU permission on DELETE /tasks and 403s
    // otherwise — this just hides a button most attendees can't use.
    const canCancelForEveryone = authUser?.role === "admin" ||
        authUser?.role === "manager" ||
        authUser?.role === "owner";
    async function removeForEveryone() {
        if (!existing?.task_id)
            return;
        if (!confirm("Cancel this meeting for EVERYONE? It's removed from every " +
            "attendee's calendar and can't be undone.")) {
            return;
        }
        setSaving(true);
        setError(null);
        try {
            // Deleting the meeting task cascades to every attendee's blocks +
            // recurring patterns (see backend delete_task).
            await api.del(`/tasks/${existing.task_id}`);
            onSaved();
            onClose();
        }
        catch (err) {
            if (err instanceof ApiError && err.status === 403) {
                setError("Only a manager or admin for this meeting's team can cancel it " +
                    "for everyone. You can still remove it from your own calendar.");
            }
            else {
                setError(err instanceof Error ? err.message : "Couldn't cancel the meeting");
            }
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsxs("div", { className: "modal-backdrop", onClick: onClose, children: [_jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("button", { type: "button", className: "modal-close", onClick: onClose, "aria-label": "Close", title: "Close", children: "\u00D7" }), _jsx("h2", { children: existing ? "Edit block" : "New block" }), !existing && (_jsxs("div", { className: "track-as", children: [_jsx("span", { className: "track-as-label", children: "Track this block as" }), _jsxs("div", { className: "track-as-seg", children: [_jsx("button", { type: "button", className: `track-as-btn ${trackAs === "project" ? "active" : ""}`, onClick: () => {
                                            setTrackAs("project");
                                            // A Kill List block is a Deep Focus block from the Kill List.
                                            setBlockType("lockout");
                                            if (duration > 60)
                                                setDuration(60);
                                        }, children: "Kill List Block" }), _jsx("button", { type: "button", className: `track-as-btn ${trackAs === "block" ? "active" : ""}`, onClick: () => {
                                            setTrackAs("block");
                                            // Flex / Personal — a free-form block, defaults to Personal.
                                            if (blockType === "lockout")
                                                setBlockType("personal");
                                        }, children: "Flex / Personal" }), _jsx("button", { type: "button", className: `track-as-btn ${trackAs === "meeting" ? "active" : ""}`, onClick: () => {
                                            setTrackAs("meeting");
                                            setBlockType("lockout");
                                        }, children: "Meeting" }), _jsx("button", { type: "button", className: `track-as-btn ${trackAs === "oneoff" ? "active" : ""}`, onClick: () => {
                                            setTrackAs("oneoff");
                                            setBlockType("lockout");
                                            if (duration > 60)
                                                setDuration(60);
                                        }, children: "One-off task" })] })] })), isMeeting && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "track-as-seg", style: { marginBottom: "0.5rem" }, children: [_jsx("button", { type: "button", className: `track-as-btn ${meetingKind === "oneoff" ? "active" : ""}`, onClick: () => setMeetingKind("oneoff"), children: "One-off meeting" }), _jsx("button", { type: "button", className: `track-as-btn ${meetingKind === "recurring" ? "active" : ""}`, onClick: () => setMeetingKind("recurring"), children: "Recurring meeting" })] }), meetingKind === "recurring" ? (_jsxs("div", { className: "flex-banner", children: [_jsx("div", { className: "flex-title", children: "Set up recurring meetings in Projects" }), _jsxs("div", { className: "flex-desc", children: ["A recurring meeting repeats on a schedule (e.g. every Monday at 10am). Create it from", " ", _jsx("strong", { children: "Projects & Meetings \u2192 + New \u2192 Recurring event" }), ", which has the full cadence editor (how often, which days, what time). Then it materializes onto each attendee's calendar automatically."] })] })) : (_jsxs(_Fragment, { children: [_jsxs("label", { children: ["Meeting name", _jsx("input", { value: oneOffTitle, onChange: (e) => setOneOffTitle(e.target.value), placeholder: "e.g. ArtistBeat Intro \u2014 Terry Jackson", autoFocus: true, required: true })] }), _jsxs("label", { children: ["Category", " ", _jsx("span", { className: "muted small", children: "\u00B7 business unit or activity" }), _jsxs("select", { value: oneOffCategory, onChange: (e) => setOneOffCategory(e.target.value), children: [_jsx("option", { value: "", children: "No category" }), businessUnits.length > 0 && (_jsx("optgroup", { label: "Business units", children: businessUnits.map((b) => (_jsx("option", { value: `bu:${b.id}`, children: b.name }, b.id))) })), activities.length > 0 && (_jsx("optgroup", { label: "Activities", children: activities.map((a) => (_jsx("option", { value: `act:${a.id}`, children: a.name }, a.id))) }))] })] }), _jsxs("label", { children: ["Attendees", _jsx(MultiSelect, { options: meetingPeople.map((p) => ({
                                                    id: p.id,
                                                    label: p.name,
                                                })), values: meetingAttendees, onChange: setMeetingAttendees, placeholder: meetingPeopleLoaded ? "Add attendees" : "Loading…", renderTrigger: (sel) => (_jsx("span", { className: "pill assignees-pill", children: sel.length === 0
                                                        ? "Pick attendees"
                                                        : `${sel.length} invited` })) })] }), _jsxs("label", { className: "inline-checkbox", children: [_jsx("input", { type: "checkbox", checked: meetingAddMe, onChange: (e) => setMeetingAddMe(e.target.checked) }), "Add me to this meeting"] }), _jsx("p", { className: "muted small", children: "Everyone invited gets this on their calendar. Anyone with a conflict at that time gets an Accept/Decline request in their Awaiting Review section instead of a double-booking." })] }))] })), isOneOff && (_jsxs(_Fragment, { children: [_jsxs("label", { children: ["Task name", _jsx("input", { value: oneOffTitle, onChange: (e) => setOneOffTitle(e.target.value), placeholder: "What needs to get done?", autoFocus: true, required: true })] }), _jsxs("label", { children: ["Category ", _jsx("span", { className: "muted small", children: "\u00B7 business unit or activity" }), _jsxs("select", { value: oneOffCategory, onChange: (e) => setOneOffCategory(e.target.value), children: [_jsx("option", { value: "", children: "No category" }), businessUnits.length > 0 && (_jsx("optgroup", { label: "Business units", children: businessUnits.map((b) => (_jsx("option", { value: `bu:${b.id}`, children: b.name }, b.id))) })), activities.length > 0 && (_jsx("optgroup", { label: "Activities", children: activities.map((a) => (_jsx("option", { value: `act:${a.id}`, children: a.name }, a.id))) }))] })] }), _jsx("div", { className: "muted small", style: { marginTop: "-0.35rem" }, children: "One merged list \u2014 tag what the work was against, without it joining a project." }), _jsxs("label", { children: ["Due date ", _jsx("span", { className: "muted small", children: "\u00B7 optional" }), _jsx("input", { type: "date", value: oneOffDue, onChange: (e) => setOneOffDue(e.target.value) })] }), _jsxs("div", { className: "flex-banner", style: { marginTop: "0.4rem" }, children: [_jsx("div", { className: "flex-title", children: "Deep Focus" }), _jsx("div", { className: "flex-desc", children: "One-off tasks are scheduled as Deep Focus blocks. Pick a start time and duration below." })] })] })), !isOneOff && !isMeeting && (_jsxs(_Fragment, { children: [linkedItemId && !existing && (_jsx("div", { className: "muted", style: { marginBottom: "0.5rem", fontSize: "0.8rem" }, children: "Linked to checklist item \u2014 completing this block can roll up progress." })), _jsx("div", { className: "row", children: _jsxs("label", { children: ["Type", _jsxs("select", { value: blockType, onChange: (e) => {
                                                const next = e.target.value;
                                                setBlockType(next);
                                                // Switching away from Deep Focus drops any kill-list
                                                // linkage we may have picked, so the prior selection
                                                // doesn't leak into a Flex/Personal save.
                                                if (next !== "lockout") {
                                                    setSelectedKillId("");
                                                    if (!existing && !prefill?.checklist_item_id) {
                                                        setLinkedItemId(null);
                                                        setTitleLocked(false);
                                                    }
                                                }
                                                // Deep Focus is capped at 60 min — snap longer durations
                                                // down so the buttons + saved payload stay consistent.
                                                if (next === "lockout" && duration > 60) {
                                                    setDuration(60);
                                                }
                                            }, children: [(trackAs !== "block" || blockType === "lockout") && (_jsx("option", { value: "lockout", children: "Deep Focus" })), _jsx("option", { value: "flex", children: "Flex" }), _jsx("option", { value: "personal", children: "Personal" })] })] }) }), !existing &&
                                blockType === "lockout" &&
                                (!linkedItemId || !!prefill?.killListItemId) && (_jsxs("div", { className: "row", children: [_jsxs("label", { children: ["From your Kill List", _jsxs("select", { value: selectedKillId, onChange: (e) => {
                                                    const id = e.target.value;
                                                    setSelectedKillId(id);
                                                    if (!id) {
                                                        // "(Pick one)" — clear any auto-filled title/link.
                                                        setTitle("");
                                                        setLinkedItemId(null);
                                                        setTitleLocked(false);
                                                        return;
                                                    }
                                                    const row = killList.find((k) => k.id === id);
                                                    if (!row)
                                                        return;
                                                    // Build the canonical "Project — Task" label and lock it
                                                    // so the user can't drift from the underlying project.
                                                    const label = row.project_name
                                                        ? `${row.project_name} — ${row.title}`
                                                        : row.title;
                                                    setTitle(label);
                                                    setTitleLocked(true);
                                                    setLinkedItemId(row.checklist_item_id);
                                                }, children: [_jsx("option", { value: "", children: killListLoading
                                                            ? "Loading…"
                                                            : killList.length === 0
                                                                ? "Nothing on your Kill List yet"
                                                                : "Pick a Kill List item…" }), killList.map((k) => (_jsx("option", { value: k.id, children: k.project_name ? `${k.project_name} — ${k.title}` : k.title }, k.id)))] })] }), killList.length === 0 && !killListLoading && (_jsx("div", { className: "muted small", style: { marginTop: "-0.3rem" }, children: "Open the scheduler, then tap \u201C+ Kill\u201D on a Mgmt Assigned Project Task or Parking Lot item to commit it for this week." }))] })), isFlex ? (_jsxs("div", { className: "flex-banner", children: [_jsx("div", { className: "flex-title", children: "Flex" }), _jsx("div", { className: "flex-desc", children: FLEX_DESCRIPTION })] })) : blockType === "lockout" ? (
                            // Deep Focus: title is sourced exclusively from the Kill List
                            // pick (or the existing block's stored title for edits). When
                            // the dropdown is also visible it already shows the selected
                            // kill row, so suppress this banner to avoid double-rendering
                            // the same info.
                            title &&
                                !(!existing &&
                                    (!linkedItemId || !!prefill?.killListItemId)) ? (_jsxs("div", { className: "flex-banner", style: { marginTop: "0.25rem" }, children: [_jsx("div", { className: "flex-title", children: "Working on" }), _jsx("div", { className: "flex-desc", children: title })] })) : null) : (_jsxs("label", { children: ["Title", titleLocked && _jsx("span", { className: "muted small", children: " \u00B7 locked from project" }), _jsx("input", { value: title, onChange: (e) => setTitle(e.target.value), required: true, autoFocus: !titleLocked, readOnly: titleLocked, "aria-readonly": titleLocked, style: titleLocked ? { opacity: 0.85, cursor: "not-allowed" } : undefined, title: titleLocked
                                            ? "This block is linked to a project task — the title is locked so you can't lose the project context."
                                            : undefined })] }))] })), !(isMeeting && meetingKind === "recurring") && (_jsxs(_Fragment, { children: [existing && (existing.task_assignee_names?.length ?? 0) > 1 && (_jsxs("div", { className: "flex-banner", style: { marginBottom: "0.5rem" }, children: [_jsx("div", { className: "flex-title", children: "\uD83D\uDD01 Shared meeting" }), _jsxs("div", { className: "flex-desc", children: ["Saving a new time moves this on every attendee\u2019s calendar", existing.task_assignee_names &&
                                                ` (${existing.task_assignee_names.join(", ")})`, "."] })] })), _jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Start date", _jsx("input", { type: "date", value: startDate, onChange: (e) => setStartDate(e.target.value) })] }), _jsxs("label", { children: ["Start time", _jsx(HourAmPmSelect, { value: startTime, onChange: setStartTime })] })] }), _jsx("div", { className: "row", children: _jsxs("label", { children: ["Duration", _jsx("div", { className: "duration-radio", children: ALLOWED_BLOCK_MINUTES
                                                // Deep Focus is capped at 60 min — long sessions need a
                                                // 15-min Flex break in between (see hint below). Other
                                                // block types keep the full 15-min ladder up to 2 hr.
                                                // Meetings are exempt from the cap (a meeting can run long).
                                                .filter((m) => blockType === "lockout" && !isMeeting ? m <= 60 : true)
                                                .map((m) => (_jsx("button", { type: "button", className: `duration-btn ${duration === m ? "active" : ""}`, onClick: () => setDuration(m), children: m === 60 ? "1 hour" : `${m} min` }, m))) })] }) }), blockType === "lockout" && !isMeeting && (_jsx("div", { className: "muted small", style: { marginTop: "-0.4rem" }, children: "Deep Focus is capped at 60 min. For longer sessions, add a 15-min Flex break between two Deep Focus blocks, or set them as two separate time blocks." })), blockType === "lockout" && duration === 60 && !isMeeting && (_jsxs("div", { className: "flex-banner", style: { marginTop: "0.25rem" }, children: [_jsx("div", { className: "flex-title", children: "Bigger than an hour?" }), _jsx("div", { className: "flex-desc", children: "If you know this project will take multiple hours, schedule it as separate Deep Focus sessions with at least a 15-min break in between \u2014 it keeps the focus sharp instead of one long grind." })] }))] })), isPersonal && (_jsxs("div", { className: "flex-banner", style: { marginTop: "0.25rem" }, children: [_jsx("div", { className: "flex-title", children: "Private block" }), _jsx("div", { className: "flex-desc", children: "This is a private block that only you can see." })] })), isPersonal && !existing && (_jsxs("label", { children: ["Add this to Personal To-Do list?", _jsxs("select", { value: addToPersonalTodo ? "yes" : "no", onChange: (e) => setAddToPersonalTodo(e.target.value === "yes"), children: [_jsx("option", { value: "no", children: "No" }), _jsx("option", { value: "yes", children: "Yes" })] })] })), !isFlex && !isOneOff && !isMeeting && (_jsxs("label", { children: ["Description", _jsx("textarea", { value: description, onChange: (e) => setDescription(e.target.value), rows: 3 })] })), !existing && blockType !== "personal" && taskCoAssignees.filter((c) => !c.is_self).length > 0 && (_jsxs("div", { className: "block-mirror-prompt", children: [_jsxs("div", { className: "block-mirror-prompt-head", children: [_jsx("strong", { children: "Also add to teammates' calendars?" }), _jsx("span", { className: "muted small", children: "Same time slot, same title \u2014 for meetings the whole team attends." })] }), (() => {
                                const others = taskCoAssignees.filter((c) => !c.is_self);
                                const allOn = others.length > 0 &&
                                    others.every((c) => mirrorTargets.has(c.user_id));
                                return (_jsxs("label", { className: "inline-checkbox", style: {
                                        marginBottom: "0.4rem",
                                        paddingBottom: "0.4rem",
                                        borderBottom: "1px solid var(--border-soft)",
                                        fontWeight: 600,
                                    }, title: "Schedule this slot for everyone on the task \u2014 each person gets credit for the BU + activity time", children: [_jsx("input", { type: "checkbox", checked: allOn, onChange: () => {
                                                setMirrorTargets(() => {
                                                    if (allOn)
                                                        return new Set();
                                                    return new Set(others.map((c) => c.user_id));
                                                });
                                            } }), _jsxs("span", { children: ["\uD83D\uDCC5 Mark as meeting", " ", _jsx("span", { className: "muted small", children: "(everyone credits the time)" })] })] }));
                            })(), _jsx("ul", { className: "block-mirror-prompt-list", children: taskCoAssignees
                                    .filter((c) => !c.is_self)
                                    .map((c) => {
                                    const checked = mirrorTargets.has(c.user_id);
                                    return (_jsx("li", { children: _jsxs("label", { className: "inline-checkbox", children: [_jsx("input", { type: "checkbox", checked: checked, onChange: () => setMirrorTargets((prev) => {
                                                        const next = new Set(prev);
                                                        if (next.has(c.user_id)) {
                                                            next.delete(c.user_id);
                                                        }
                                                        else {
                                                            next.add(c.user_id);
                                                        }
                                                        return next;
                                                    }) }), _jsx("span", { children: c.user_name })] }) }, c.user_id));
                                }) })] })), !existing &&
                        prefill?.offerSlots &&
                        !conflict &&
                        suggestedSlots.length > 0 && (_jsxs("div", { className: "conflict-suggestions", style: { marginTop: "0.4rem" }, children: [_jsx("div", { className: "conflict-card-label", children: "Nearest open slots \u2014 tap to reschedule into one" }), _jsx("div", { className: "row", style: {
                                    flexWrap: "wrap",
                                    gap: "0.4rem",
                                    marginTop: "0.35rem",
                                }, children: suggestedSlots.map((s) => {
                                    const start = new Date(s.start_at);
                                    const label = start.toLocaleString([], {
                                        weekday: "short",
                                        month: "short",
                                        day: "numeric",
                                        hour: "numeric",
                                        minute: "2-digit",
                                    });
                                    return (_jsx("button", { type: "button", className: "dow-btn", onClick: () => applySuggestedSlot(s.start_at), title: "Use this time", children: label }, s.start_at));
                                }) })] })), error && _jsx("div", { className: "error", children: error }), existing?.task_id && (_jsxs("div", { className: "block-task-progress", children: [_jsxs("div", { className: "block-task-progress-head", children: [_jsx("span", { className: "muted small", children: "Task progress" }), _jsxs("span", { className: "small", style: { fontWeight: 600 }, children: [taskProgress ?? 0, "%"] }), taskProgress === 100 && (_jsx("span", { className: "pill ok", title: "This task is complete", children: "\u2713 Complete" }))] }), _jsx(ProgressBar, { value: taskProgress ?? 0 }), _jsxs("div", { className: "block-task-progress-actions", children: [_jsx("button", { type: "button", onClick: () => setLinkedTaskProgress(100), disabled: saving || taskProgress === 100, className: "btn-primary", title: "Mark the underlying task complete (sets progress to 100%)", children: taskProgress === 100 ? "Task complete" : "Mark task complete" }), taskProgress === 100 && (_jsx("button", { type: "button", onClick: () => setLinkedTaskProgress(0), disabled: saving, className: "btn-link", title: "Re-open the task \u2014 sets progress back to 0%", children: "Re-open task" }))] })] })), existing?.project_id && (_jsxs("div", { className: "block-project-info", children: [linkedProject && (_jsxs("div", { className: "block-project-head", children: [_jsx("strong", { className: "block-project-name", children: linkedProject.name }), (linkedProject.assignee_names?.length ?? 0) > 0 ? (_jsxs("div", { className: "muted small", children: ["Team:", " ", linkedProject.assignee_names.slice(0, 6).join(", "), (linkedProject.assignee_names?.length ?? 0) > 6 &&
                                                ` +${(linkedProject.assignee_names.length - 6)} more`] })) : (_jsx("div", { className: "muted small", children: "No one is on this project yet." }))] })), _jsx("button", { type: "button", className: "btn-primary", onClick: () => {
                                    window.open(`/projects?project=${encodeURIComponent(existing.project_id)}`, "_blank", "noopener,noreferrer");
                                }, title: "Open this project in a new tab", children: "Open project \u2197" })] })), _jsxs("div", { className: "modal-actions", children: [existing &&
                                isSharedMeeting &&
                                canCancelForEveryone &&
                                existing.task_id ? (
                            // Shared meeting + management: the default delete cancels the
                            // meeting for EVERY attendee, so it can't linger on other
                            // people's calendars (the reported bug). The my-calendar-only
                            // removes drop to secondary links beside it.
                            _jsxs(_Fragment, { children: [_jsx("button", { onClick: removeForEveryone, className: "btn-danger", disabled: saving, title: "Cancels this meeting for every attendee", children: existing.recurring_pattern_id
                                            ? "Cancel meeting for everyone"
                                            : "Delete for everyone" }), _jsx("button", { onClick: remove, className: "btn-link", disabled: saving, title: "Removes it from only your calendar \u2014 other attendees keep it", children: existing.recurring_pattern_id
                                            ? "Just remove this occurrence from mine"
                                            : "Just remove from my calendar" }), existing.recurring_pattern_id && (_jsx("button", { onClick: removeWholeSeries, className: "btn-link", disabled: saving, title: "Removes the whole series from only your calendar", children: "Just remove my series" }))] })) : (_jsxs(_Fragment, { children: [existing && (_jsx("button", { onClick: remove, className: "btn-danger", disabled: saving, children: existing.recurring_pattern_id
                                            ? "Delete this occurrence"
                                            : isSharedMeeting
                                                ? "Remove from my calendar"
                                                : "Delete" })), existing?.recurring_pattern_id && (_jsx("button", { onClick: removeWholeSeries, className: "btn-danger", disabled: saving, title: "Removes the recurring meeting and every block on your calendar", children: isSharedMeeting
                                            ? "Remove series from my calendar"
                                            : "Delete entire series" }))] })), existing &&
                                onPromptProgress &&
                                !existing.is_completed &&
                                new Date(existing.end_at).getTime() <= Date.now() && (_jsx("button", { onClick: () => {
                                    onPromptProgress(existing);
                                    onClose();
                                }, disabled: saving, className: "block-editor-complete-btn", title: "Mark this block done \u2014 or 'Not yet' if it ran long", children: "Complete?" })), _jsx("button", { onClick: onClose, disabled: saving, children: "Cancel" }), !(isMeeting && meetingKind === "recurring") && (_jsx("button", { onClick: save, disabled: saving ||
                                    ((isOneOff || isMeeting) && oneOffTitle.trim().length === 0), className: "btn-primary", children: saving
                                    ? isOneOff || isMeeting
                                        ? "Adding…"
                                        : "Saving…"
                                    : isMeeting
                                        ? "Create meeting"
                                        : isOneOff
                                            ? "Add to calendar"
                                            : "Save" }))] })] }), conflict && (_jsx("div", { className: "modal-backdrop", onClick: () => {
                    setConflict(null);
                    setSuggestedSlots([]);
                }, 
                // Sit above the regular block-editor backdrop so it can't be
                // dismissed by clicking through.
                style: { zIndex: 200 }, children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "Time slot already booked" }), _jsx("p", { className: "muted small", style: { marginTop: "-0.5rem" }, children: "That window overlaps an existing block on your calendar. Pick which one to keep \u2014 or jump to the next open slot." }), _jsxs("div", { className: "conflict-pair", children: [_jsxs("div", { className: "conflict-card", children: [_jsx("div", { className: "conflict-card-label", children: "Already on your calendar" }), _jsx("strong", { children: conflict.title }), _jsxs("div", { className: "muted small", children: [new Date(conflict.start_at).toLocaleString([], {
                                                    weekday: "short",
                                                    month: "short",
                                                    day: "numeric",
                                                    hour: "numeric",
                                                    minute: "2-digit",
                                                }), " ", "\u2013", " ", new Date(conflict.end_at).toLocaleTimeString([], {
                                                    hour: "numeric",
                                                    minute: "2-digit",
                                                })] })] }), _jsxs("div", { className: "conflict-card", children: [_jsx("div", { className: "conflict-card-label", children: "New block you're adding" }), _jsx("strong", { children: title || "(untitled)" }), _jsxs("div", { className: "muted small", children: [fromLocalDateTimeParts(startDate, startTime).toLocaleString([], {
                                                    weekday: "short",
                                                    month: "short",
                                                    day: "numeric",
                                                    hour: "numeric",
                                                    minute: "2-digit",
                                                }), " ", "\u00B7 ", duration, " min"] })] })] }), suggestedSlots.length > 0 && (_jsxs("div", { className: "conflict-suggestions", style: { marginTop: "0.8rem" }, children: [_jsx("div", { className: "conflict-card-label", children: "Nearest open slots" }), _jsx("div", { className: "row", style: {
                                        flexWrap: "wrap",
                                        gap: "0.4rem",
                                        marginTop: "0.35rem",
                                    }, children: suggestedSlots.map((s) => {
                                        const start = new Date(s.start_at);
                                        const label = start.toLocaleString([], {
                                            weekday: "short",
                                            month: "short",
                                            day: "numeric",
                                            hour: "numeric",
                                            minute: "2-digit",
                                        });
                                        return (_jsx("button", { type: "button", className: "dow-btn", onClick: () => {
                                                applySuggestedSlot(s.start_at);
                                                setConflict(null);
                                                setSuggestedSlots([]);
                                            }, title: "Use this time", children: label }, s.start_at));
                                    }) }), _jsx("p", { className: "muted small", style: { marginTop: "0.4rem" }, children: "Pick one to drop the new block there \u2014 then hit Save." })] })), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: () => {
                                        setConflict(null);
                                        setSuggestedSlots([]);
                                    }, disabled: saving, children: "Keep existing" }), _jsx("button", { onClick: resolveByReplacing, className: "btn-primary", disabled: saving, children: saving ? "Replacing…" : "Replace with new" })] })] }) }))] }));
}
